'use client';

import React, {
  useContext,
  createContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';

import { UserViewModel, SeturLoyaltyPointsViewModel } from '@api/v3';
import { removeStorageItems } from '@utils/utils';
import { clearInsiderUserInfo, setInsiderUserInfo } from '@utils/insider';
import { clearBasketStorage } from '@utils/basket';
import { TOKEN_EXPIRATION_KEY } from '@components/views/login/constants';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';

import { Action, ACTIONS, AuthState, LoginInfo } from './types';

export type { LoginInfo };

const initialValues = {
  userInfo: {
    id: 0,
    firstName: '',
    lastName: '',
    isTurkishCitizen: true,
    userName: '',
    isSeturEmployee: false,
  },
  tokenExpirationDate: null,
  loyaltyPoints: null,
};

type AuthDispatch = (action: Action) => void;

const AuthStateContext = createContext<AuthState | undefined>(undefined);
export const AuthDispatchContext = createContext<AuthDispatch | undefined>(
  undefined
);

const getStoredUserInfo = () => {
  const userInfo = getSafeLocalStorageItem('userInfo');
  if (!userInfo) {
    return {};
  }
  try {
    return JSON.parse(userInfo);
  } catch (error) {
    return {};
  }
};
const authReducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case ACTIONS.SET_USER_INFO:
      return { ...state, userInfo: action.payload };
    case ACTIONS.SET_LOYALTY_POINTS:
      return { ...state, loyaltyPoints: action.payload };
    case ACTIONS.SET_TOKEN_EXPIRATION_DATE:
      return { ...state, tokenExpirationDate: action.payload };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const AuthProvider = ({
  children,
  loginInfo = {} as LoginInfo,
}: React.PropsWithChildren<{ loginInfo?: LoginInfo }>) => {
  const [state, dispatch] = useReducer(authReducer, {
    ...initialValues,
    userInfo: {
      ...initialValues.userInfo,
      ...(loginInfo.b2CUser ?? {}),
      id: initialValues.userInfo.id || +loginInfo?.b2CUser?.id,
    },
  });

  const tokenExpiration = getSafeLocalStorageItem(TOKEN_EXPIRATION_KEY);

  const seturPoints = getSafeLocalStorageItem('seturPoints');

  useEffect(() => {
    if (state?.userInfo?.id) {
      setSafeLocalStorageItem('userId', `${state?.userInfo?.id}`);
      setSafeLocalStorageItem(
        'userInfo',
        JSON.stringify({
          lastName: state?.userInfo?.lastName ?? '',
          firstName: state?.userInfo?.firstName ?? '',
          phone: state?.userInfo?.phone ?? '',
          id: state?.userInfo?.id ?? '',
          userName: state?.userInfo?.userName ?? '',
        })
      );
      setInsiderUserInfo(state.userInfo);
    }
  }, [state?.userInfo]);

  useEffect(() => {
    if (seturPoints) {
      dispatch({
        type: ACTIONS.SET_LOYALTY_POINTS,
        payload: JSON.parse(seturPoints),
      });
    }
  }, [seturPoints]);

  useEffect(() => {
    if (tokenExpiration) {
      dispatch({
        type: ACTIONS.SET_TOKEN_EXPIRATION_DATE,
        payload: new Date(tokenExpiration),
      });
    }
  }, [tokenExpiration]);

  // Allow access to userInfo static pages and pages reached from other domains that do not send expected cookies
  // we try to read user information from local storage
  const userFromCache: UserViewModel = getStoredUserInfo();
  const derivedUserInfo = state.userInfo?.firstName
    ? state.userInfo
    : userFromCache;
  return (
    <AuthStateContext.Provider
      value={{ ...state, userInfo: derivedUserInfo, loginInfo }}
    >
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useAuthState = <T extends any = UserViewModel>(): AuthState & {
  userInfo: T;
} => {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within a AuthProvider');
  }
  return context as AuthState & {
    userInfo: T;
  };
};

export const useSetUserInfo = (): ((
  payload: UserViewModel
) => Promise<void>) => {
  const dispatch = useContext(AuthDispatchContext);
  const setUserInfo = useCallback(
    async (payload: UserViewModel) => {
      if (dispatch) {
        dispatch({ type: ACTIONS.SET_USER_INFO, payload: payload });
      }
    },
    [dispatch]
  );
  if (dispatch === undefined) {
    throw new Error('useSetUserInfo must be used within a AuthProvider');
  }
  return setUserInfo;
};

export const useSetTokenExpirationDate = (): ((payload: Date) => void) => {
  const dispatch = useContext(AuthDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetUserInfo must be used within a AuthProvider');
  }
  return (payload: Date) =>
    dispatch({ type: ACTIONS.SET_TOKEN_EXPIRATION_DATE, payload: payload });
};

export const useSetLoyaltyPoints = (): ((
  payload: SeturLoyaltyPointsViewModel
) => void) => {
  const dispatch = useContext(AuthDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetUserInfo must be used within a AuthProvider');
  }
  return (payload: SeturLoyaltyPointsViewModel) =>
    dispatch({ type: ACTIONS.SET_LOYALTY_POINTS, payload: payload });
};
const isLoggingOutFromPrivatePage = (pathname: string) =>
  pathname.startsWith('/profil');

export const useClearDispatch = (): (() => Promise<void>) => {
  const dispatch = useContext(AuthDispatchContext);

  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a AuthProvider');
  }
  const clear = async () => {
    clearBasketStorage();

    setSafeLocalStorageItem('loginAction', 'out');
    sessionStorage.removeItem('selectedPromotion');
    sessionStorage.removeItem('selectedCoupon');
    sessionStorage.removeItem('switchError');
    removeSafeLocalStorageItem('activePaymentMethod');
    dispatch({ type: ACTIONS.CLEAR });
    await fetch('/api/logout');
    dispatch({ type: ACTIONS.CLEAR });
    removeStorageItems([
      'userInfo',
      'tokenExpiration',
      'loyalties',
      'userId',
      isLoggingOutFromPrivatePage(location.pathname) ? '' : 'loginAction',
      'seturPoints',
      'loyaltyUserInfo',
      'club_expired',
      'CloseIconControl',
      'loginSessionExpiration',
      'token',
      'refreshToken',
    ]);
    window.history.replaceState({}, '', window.location.pathname);
    clearInsiderUserInfo();
    window.location.reload();
    setSafeLocalStorageItem('refreshTabs', JSON.stringify(Date.now())); // Logout for normal accounts
  };
  return useCallback(clear, [dispatch]);
};

export default AuthProvider;

export const DISCONNECTION_REASONS = {
  SESSION_EXPIRATION: 'SESSION_EXPIRATION',
};
export const DISCONNECTION_REASON_STORAGE_KEY = 'DISCONNECTION_REASON';
